import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { moduleConfig } from './config';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../../services/paginatedList';

export interface SpareItemAttachment {
  id: number;
  fileName: string;
  mimeType?: string | null;
  fileType?: string | null;
  fileSize?: number | null;
  uploadedAt?: string | null;
}

export interface SpareItem {
  id: number;
  libraryId?: number | null;
  library?: { id: number; name: string } | null;
  categoryId?: number | null;
  category?: { id: number; name: string; abrv?: string | null } | null;
  vendorCode?: string | null;
  name: string;
  drawingNo?: string | null;
  serialNo?: string | null;
  notes?: string | null;
  isActive: boolean;
  isCritical: boolean;
  isHazardousMaterial: boolean;
  stockMin?: number | null;
  stockMax?: number | null;
  createdAt: string;
  updatedAt: string;
  attachments?: SpareItemAttachment[];
  FileAttachment?: SpareItemAttachment[];
  /** Non-deleted attachment count from list API */
  fileCount?: number;
  _count?: { attachments?: number };
  /** True when this item appears on one or more spare requisition lines. */
  isUsed?: boolean;
}

export const useSpareItems = (
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<PaginatedApiResponse<SpareItem> | SpareItem[]>(
    `${moduleConfig.module.api.list}${q}`,
    fetcher,
    { revalidateOnFocus: true },
  );
  const normalized = normalizePaginatedListResponse<SpareItem>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useSpareItem = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<SpareItem>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
};
